import z from 'zod';
import { isValidCnpj, normalizeDocument } from '@seller-hermes/utils/cnpj';

export enum SellerDocumentTypes {
  CPF = 'CPF',
  CNPJ = 'CNPJ',
  PASSPORT = 'PASSPORT',
  RUT = 'RUT',
  CI = 'CI',
  CC = 'CC',
  CE = 'CE',
  NIT = 'NIT',
  RFC = 'RFC',
  DNI = 'DNI',
  LC = 'LC',
  LE = 'LE',
  CUIT = 'CUIT',
  RUC = 'RUC',
}

export enum SellerCountries {
  ARG = 'ARG',
  BRA = 'BRA',
  CHL = 'CHL',
  COL = 'COL',
  MEX = 'MEX',
  PER = 'PER',
}

export enum CountryFlags {
  ARG = 'ar',
  BRA = 'br',
  CHL = 'cl',
  COL = 'co',
  MEX = 'mx',
  PER = 'pe',
}

export const newSellerSchema = z
  .object({
    email: z.string().email({ message: 'Email inválido' }),
    phone: z
      .string()
      .transform((val) => val.replace(/[^0-9]/g, ''))
      .refine((data) => data.trim() !== '', { message: 'Obrigatório' }),
    document_type: z.nativeEnum(SellerDocumentTypes),
    document_number: z
      .string()
      .refine((data) => data.trim() !== '', { message: 'Obrigatório' }),
    trading_name: z
      .string()
      .refine((data) => data.trim() !== '', { message: 'Obrigatório' }),
    identifier: z.string().optional(),
    state_registration: z
      .string()
      .optional()
      .transform((val) => (val ? val.replace(/[^0-9]/g, '') : undefined)),
    tester: z.boolean().default(false).optional(),
    country: z.nativeEnum(SellerCountries),
    timezone: z.string(),
    address: z.object({
      street: z
        .string()
        .refine((data) => data.trim() !== '', { message: 'Obrigatório' }),
      street_number: z
        .string()
        .refine((data) => data.trim() !== '', { message: 'Obrigatório' }),
      complement: z.string().optional(),
      reference: z.string().optional(),
      neighborhood: z
        .string()
        .refine((data) => data.trim() !== '', { message: 'Obrigatório' }),
      zip_code: z
        .string()
        .refine((data) => data.trim() !== '', { message: 'Obrigatório' }),
      city: z
        .string()
        .refine((data) => data.trim() !== '', { message: 'Obrigatório' }),
      state: z
        .string()
        .refine((data) => data.trim() !== '', { message: 'Obrigatório' }),
      // Geolocalização opcional — quando o usuário ajusta o pino no mapa
      // (ou geocodifica manualmente) no wizard de primeira filial. Sem valor,
      // o backend geocodifica a partir do endereço na criação.
      latitude: z.number().optional(),
      longitude: z.number().optional(),
    }),
    operation_hours: z
      .record(
        z.string(),
        z.object({
          open: z.string().nonempty('O horário de abertura é obrigatório'),
          close: z.string().nonempty('O horário de fechamento é obrigatório'),
        }),
      )
      .optional()
      .nullable(),
  })
  .transform((data) => {
    if (data.country === SellerCountries.BRA) {
      return {
        ...data,
        address: {
          ...data.address,
          zip_code: data.address.zip_code?.replace(/[^0-9]/g, ''),
        },
      };
    }
    return data;
  })
  .transform((data) => {
    if (data.document_type === SellerDocumentTypes.CNPJ) {
      return {
        ...data,
        document_number: normalizeDocument(data.document_number),
      };
    }
    return data;
  })
  .superRefine(({ address, document_type, document_number, country }, ctx) => {
    switch (country) {
      case SellerCountries.BRA:
        if (!/\d{8}/.test(address.zip_code)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['address.zip_code'],
            message: `CEP deve conter 8 dígitos. Atualmente ${address.zip_code.length}`,
          });
        }
        if (document_type === SellerDocumentTypes.CNPJ && !isValidCnpj(document_number)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['document_number'],
            message: 'CNPJ inválido. Verifique os 14 caracteres e os dígitos verificadores.',
          });
        }
    }
  });
